/**
 * heroCarouselLayout.js
 *
 * Pure geometry helpers for the storefront hero carousel (fix/hero-carousel-
 * aspect-no-crop). The carousel must NEVER crop a slide image. Instead:
 *
 *   - The hero band has a fixed WIDTH = the storefront content column width.
 *   - The band HEIGHT = the height of the WIDEST slide (largest width:height
 *     aspect ratio) when scaled to fill the full column width. The widest
 *     image is the SHORTEST when fit to width, so the band is as short as the
 *     widest image needs: bandHeight = columnWidth / maxAspectRatio.
 *   - The widest image fills the width edge-to-edge with no bars, no crop.
 *   - Every narrower / taller-aspect image is scaled to FIT the band height
 *     (object-fit: contain against the fixed band height), leaving LEFT/RIGHT
 *     white padding. Never cropped.
 *   - No image is ever UPSCALED beyond its natural pixel size (upscaling looks
 *     blurry). Downscaling to fit is fine; a slide smaller than the band
 *     renders at natural size, centered, letterboxed with white padding.
 *
 * These functions are DOM-free so they can be unit-tested directly. The Vue
 * component owns loading each slide's naturalWidth/naturalHeight and observing
 * the column width; it delegates the arithmetic here.
 */

/**
 * Aspect ratio (width / height) of a single slide's natural dimensions.
 * Returns null when the slide has no usable (positive, finite) dimensions --
 * e.g. its image has not loaded yet -- so callers can skip it from the max.
 *
 * @param {{naturalWidth?: number, naturalHeight?: number}} slide
 * @returns {number|null}
 */
export function slideAspectRatio(slide) {
  if (!slide) {
    return null;
  }
  const w = Number(slide.naturalWidth);
  const h = Number(slide.naturalHeight);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return null;
  }
  return w / h;
}

/**
 * The largest aspect ratio (widest slide) across all slides whose dimensions
 * have loaded. Slides without usable dimensions are ignored. Returns null when
 * NO slide has loaded dimensions yet, so the caller can hold a neutral height
 * and avoid a layout jump to 0.
 *
 * @param {Array<{naturalWidth?: number, naturalHeight?: number}>} slides
 * @returns {number|null}
 */
export function maxAspectRatio(slides) {
  if (!Array.isArray(slides) || slides.length === 0) {
    return null;
  }
  let max = null;
  for (const slide of slides) {
    const ratio = slideAspectRatio(slide);
    if (ratio === null) {
      continue;
    }
    if (max === null || ratio > max) {
      max = ratio;
    }
  }
  return max;
}

/**
 * The hero band height (px) for a given column width and max aspect ratio.
 * The widest slide, scaled to fill the full column width, is exactly this
 * tall; every other slide is shorter and gets side padding.
 *
 * Returns null when either input is missing/non-positive so the caller can
 * fall back to a neutral placeholder height rather than collapse to 0.
 *
 * An optional ``maxHeight`` caps the result so a tall/narrow-aspect slide can
 * never make the hero band tower over the page: the band reads as a banner
 * caption rather than a full-screen billboard. A missing/non-positive
 * ``maxHeight`` leaves the height uncapped (legacy behavior).
 *
 * @param {number} columnWidth  content column width in px
 * @param {number|null} maxAspect  largest slide aspect ratio (width/height)
 * @param {number} [maxHeight]  optional upper bound (px) on the band height
 * @returns {number|null}
 */
export function bandHeight(columnWidth, maxAspect, maxHeight) {
  const w = Number(columnWidth);
  const a = Number(maxAspect);
  if (!Number.isFinite(w) || w <= 0) {
    return null;
  }
  if (!Number.isFinite(a) || a <= 0) {
    return null;
  }
  const height = w / a;
  const cap = Number(maxHeight);
  if (Number.isFinite(cap) && cap > 0) {
    return Math.min(height, cap);
  }
  return height;
}

/**
 * Aspect ratio (width/height) at or above which a slide is treated as a
 * BANNER -- one wide image meant to span the page, with any headline baked
 * into its own pixels -- rather than as product art to be shown beside the
 * store name.
 *
 * WHY 2.2, from the real measured art rather than taste:
 *
 *   T+L product slides   495x497 = 1.00   \
 *                        338x484 = 0.70    > the widest is 1.55
 *                        499x322 = 1.55   /
 *   ordinary 16:9 photo          = 1.78   (the commonest landscape ratio)
 *   T+L banner crop     1448x451 = 3.21
 *
 * 2.2 sits in the empty gap between the two clusters, with ~0.4 of headroom
 * above 16:9 and ~1.0 of clearance below the banner. A distributor uploading
 * a normal wide photograph therefore keeps the split band; only art that is
 * genuinely banner-shaped takes the full bleed. Nothing real measures between
 * 1.78 and 3.21, so no observed asset sits near the boundary.
 *
 * This is what lets the layout mode be DERIVED rather than configured -- no
 * new Branding field a distributor has to understand, one mechanism per
 * concept (docs/design-philosophy.md).
 */
export const BANNER_ASPECT_THRESHOLD = 2.2;

/**
 * Whether a measured aspect ratio is banner-shaped.
 *
 * An unmeasured or invalid aspect (image not loaded yet, failed to load,
 * zero/NaN dimensions) is NOT a banner: with no evidence the caller should
 * hold the layout that already ships rather than claim a full-bleed takeover
 * it may have to retract a frame later.
 *
 * @param {number|null} aspect  width/height, e.g. from maxAspectRatio()
 * @returns {boolean}
 */
export function isBannerAspect(aspect) {
  const a = Number(aspect);
  if (!Number.isFinite(a) || a <= 0) {
    return false;
  }
  return a >= BANNER_ASPECT_THRESHOLD;
}

/**
 * The rendered WIDTH (px) of a full-bleed banner, given the band width, the
 * banner's aspect ratio and the band's height cap.
 *
 * BANNER LETTERBOX (2026-08-26, Jake) -- reverses the 2026-08-15 decision.
 *
 * The band height is capped (HERO_BANNER_MAX_HEIGHT) so a banner cannot
 * tower over the page. Once that cap BINDS, the band is shorter than the
 * banner's own ratio wants, and the old `object-fit: cover` filled it by
 * slicing the overflow off the top and bottom -- at a 2560px viewport it
 * discarded 177px of artwork, at 3440px it discarded 451px. When the
 * headline is baked into the pixels (the T+L / ACME comp) that crop cuts
 * the words in half.
 *
 * The fix: render the banner at the width its CAPPED HEIGHT can show whole
 * (cappedHeight * aspect) and let the leftover viewport width become margin
 * at the sides. Below the cap nothing changes -- the banner still fills the
 * width edge to edge, which is the case full-bleed exists for and the one
 * Jake explicitly kept.
 *
 * Returns null when the width or aspect is unknown so the caller can hold
 * its neutral fallback rather than collapse (principle 5: safe fallback).
 *
 * @param {number} bandWidth   the band's measured width in px
 * @param {number|null} aspect the banner's width/height ratio
 * @param {number} [maxHeight] the band's height cap in px; <=0 means uncapped
 * @returns {number|null}
 */
export function bannerRenderWidth(bandWidth, aspect, maxHeight) {
  const w = Number(bandWidth);
  const a = Number(aspect);
  if (!Number.isFinite(w) || w <= 0) {
    return null;
  }
  if (!Number.isFinite(a) || a <= 0) {
    return null;
  }
  const cap = Number(maxHeight);
  if (!Number.isFinite(cap) || cap <= 0) {
    // No height cap -- nothing can force a crop, so fill the width.
    return w;
  }
  // The widest the banner can be drawn without exceeding the capped height.
  const widthAtCappedHeight = cap * a;
  return Math.min(w, widthAtCappedHeight);
}

/**
 * Upper bound (px) on a FULL-BLEED BANNER's rendered height. One constant for
 * every full-bleed banner on the storefront: the hero's banner mode
 * (StorefrontHero) and the category page header (StorefrontBanner), so the two
 * cannot drift apart (GH-3633).
 *
 * 620 lets a banner render at its natural ratio on every common desktop width
 * (a 2000px viewport is the first that would be capped) while keeping the band
 * under the fold on a laptop. Once the cap binds the banner letterboxes
 * horizontally (margin at the sides) -- it is never cropped.
 */
export const HERO_BANNER_MAX_HEIGHT = 620;
